/**
 * Done. / ChiroCPD — Dashboard Page (/dashboard)
 *
 * Progress overview: annual hours, cycle hours, AI generations remaining.
 * Recent activities list, Annual Plan preview.
 * All data from /api/dashboard endpoint.
 */

import { useState, useEffect } from 'react';
import { Link } from 'wouter';
import { AppLayout } from '@/layouts/AppLayout';
import { StatCard } from '@/components/features/StatCard';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { AILabel } from '@/components/ui/AILabel';
import { Button } from '@/components/ui/Button';
import { api, ApiError } from '@/lib/api';
import { formatDate, formatHours, hoursRemainingLabel, hourCapHealth, aiCapHealth } from '@/lib/utils';
import type { DashboardData } from '@/lib/types';

function DashboardContent(): React.ReactElement {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function fetch(): Promise<void> {
      try {
        const result = await api.get<DashboardData>('/api/dashboard');
        if (!cancelled) setData(result);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) return;
        setError(err instanceof Error ? err.message : 'Failed to load dashboard');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void fetch();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <svg className="h-8 w-8 animate-spin text-[var(--color-done-500)]" fill="none" viewBox="0 0 24 24" aria-label="Loading dashboard…">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-[var(--radius-md)] bg-[var(--color-error-100)] px-4 py-3 text-sm text-red-700" role="alert">
        {error}
        <Button variant="ghost" size="sm" onClick={() => { window.location.reload(); }} className="ml-3">
          Retry
        </Button>
      </div>
    );
  }

  if (!data) return <></>;

  const { capStatus, recentActivities, annualPlan } = data;

  return (
    <div className="flex flex-col gap-8">
      {/* Page heading */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-[var(--text-primary)]">Dashboard</h1>
        <Link href="/activities">
          <Button variant="primary" size="sm">+ Log Activity</Button>
        </Link>
      </div>

      {/* Stat cards */}
      <section aria-label="CPD progress overview">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard
            label="Annual hours"
            value={formatHours(capStatus.annualHoursLogged)}
            unit={`/ ${String(capStatus.annualHourCap)} hrs`}
            status={hourCapHealth(capStatus)}
            description={hoursRemainingLabel(capStatus.annualHoursLogged, capStatus.annualHourCap)}
          />
          <StatCard
            label="2-year cycle"
            value={formatHours(capStatus.cycleHoursLogged)}
            unit={`/ ${String(capStatus.cycleHourCap)} hrs`}
            status={hourCapHealth(capStatus)}
            description={`${String(capStatus.currentYear)}–${String(capStatus.cycleStartYear + 1)} cycle`}
          />
          <StatCard
            label="AI drafts"
            value={capStatus.aiGenerationCap - capStatus.aiGenerationsUsed}
            unit={`/ ${String(capStatus.aiGenerationCap)} remaining`}
            status={aiCapHealth(capStatus)}
            description="Resets monthly"
          />
        </div>
      </section>

      {/* Annual progress bar */}
      <section
        aria-label="Annual CPD hours progress"
        className="rounded-[var(--radius-md)] bg-[var(--surface-base)] p-5 shadow-sm"
      >
        <h2 className="text-sm font-semibold text-[var(--text-primary)] mb-3">
          {String(capStatus.currentYear)} Annual Progress
        </h2>
        <ProgressBar
          current={capStatus.annualHoursLogged}
          max={capStatus.annualHourCap}
          label={`Annual CPD hours: ${String(capStatus.annualHoursLogged)} of ${String(capStatus.annualHourCap)}`}
        />
      </section>

      {/* Annual Plan preview */}
      {annualPlan && (
        <section
          aria-label="Annual Plan"
          className="rounded-[var(--radius-md)] bg-[var(--surface-base)] p-5 shadow-sm"
        >
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-[var(--text-primary)]">
              Annual CPD Plan — {annualPlan.year}
            </h2>
            {annualPlan.aiGenerated && <AILabel />}
          </div>
          <p className="text-sm text-[var(--text-secondary)] leading-relaxed line-clamp-4">
            {annualPlan.planText}
          </p>
          <Link href="/export" className="mt-3 inline-block text-sm font-medium text-[var(--color-done-600)] hover:underline">
            View export fields →
          </Link>
        </section>
      )}

      {/* Recent activities */}
      <section aria-label="Recent activities">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-[var(--text-primary)]">Recent Activities</h2>
          <Link href="/activities" className="text-sm text-[var(--color-done-600)] hover:underline">
            View all →
          </Link>
        </div>

        {recentActivities.length === 0 ? (
          <div className="rounded-[var(--radius-md)] bg-[var(--surface-muted)] px-4 py-6 text-center">
            <p className="text-sm text-[var(--text-secondary)]">No activities logged yet.</p>
            <Link href="/activities" className="mt-2 inline-block text-sm font-medium text-[var(--color-done-600)] hover:underline">
              Log your first activity →
            </Link>
          </div>
        ) : (
          <div className="rounded-[var(--radius-md)] bg-[var(--surface-base)] shadow-sm divide-y divide-[var(--border-subtle)]">
            {recentActivities.map(activity => (
              <div key={activity.id} className="flex items-center justify-between gap-4 px-4 py-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[var(--text-primary)] truncate">{activity.title}</p>
                  <p className="text-xs text-[var(--text-muted)]">
                    {formatDate(activity.activityDate)} · {activity.activityTypeName}
                  </p>
                </div>
                <span className="text-sm font-semibold text-[var(--text-secondary)] shrink-0">
                  {formatHours(activity.hours)} hrs
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export function DashboardPage(): React.ReactElement {
  return (
    <AppLayout currentRoute="/dashboard">
      <DashboardContent />
    </AppLayout>
  );
}
